import React from "react";
import { Link } from "react-router-dom";
import { Head } from "@unhead/react";
import { afTrack } from "../analytics/appsflyer";
import { getAccessToken } from "../api";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function smoothPrices(arr) {
  return arr.map((p, i) => {
    let sum = 0, count = 0;
    for (let j = i - 1; j <= i + 1; j++) {
      if (arr[j]) { sum += arr[j].price; count++; }
    }
    return { ...p, price: sum / count };
  });
}

function SatsTooltip() {
  const [open, setOpen] = React.useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        style={{ background: "none", border: "none", cursor: "pointer", padding: "0 2px", color: "#a1a1aa", fontSize: "0.85em", lineHeight: 1 }}
        aria-label="What is a sat?"
      >
        ⓘ
      </button>
      {open && (
        <span style={{ position: "absolute", bottom: "125%", left: "50%", transform: "translateX(-50%)", background: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px", padding: "6px 10px", fontSize: "0.75rem", color: "#d4d4d8", whiteSpace: "nowrap", zIndex: 50, pointerEvents: "none" }}>
          Sat is the smallest unit of Bitcoin
        </span>
      )}
    </span>
  );
}

function useCountUp(target, duration = 1400) {
  const [value, setValue] = React.useState(0);
  const raf  = React.useRef(null);
  const from = React.useRef(0);
  const start = React.useRef(null);

  React.useEffect(() => {
    if (target === 0) { setValue(0); return; }
    from.current  = value;
    start.current = null;
    const animate = (ts) => {
      if (!start.current) start.current = ts;
      const p     = Math.min((ts - start.current) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from.current + (target - from.current) * eased));
      if (p < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [target]);

  return value;
}

function formatSignedSats(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n);
  return `${rounded > 0 ? "+" : ""}${rounded.toLocaleString()}`;
}

function formatSignedPct(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00%";
  return `${n > 0 ? "+" : ""}${n.toFixed(digits)}%`;
}

const CADENCES = [
  { value: "daily",     label: "Daily",     sub: "Every day"    },
  { value: "weekly",    label: "Weekly",    sub: "Every week"   },
  { value: "bi-weekly", label: "Bi-weekly", sub: "Every 2 weeks"},
];

const PRESETS = [10, 25, 50, 100, 250, 500];

// ─── Calculator page ──────────────────────────────────────────────────────────

export default function Calculator() {
  const [cadence,     setCadence]     = React.useState("bi-weekly");
  const [buyUsd,      setBuyUsd]      = React.useState(100);
  const [isCustom,    setIsCustom]    = React.useState(false);
  const [customVal,   setCustomVal]   = React.useState("");
  const [result,      setResult]      = React.useState(null);
  const [loading,     setLoading]     = React.useState(false);
  const [err,         setErr]         = React.useState(null);
  const [hasRun,      setHasRun]      = React.useState(false);
  const [biweeklyPct, setBiweeklyPct] = React.useState(null);

  const resultRef = React.useRef(null);
  const didTrackView = React.useRef(false);
  const isLoggedIn = !!getAccessToken();

  const seoTitle =
    "Bitcoin DCA Calculator: Compare Regular vs Smart DCA | SatStacker";

  const seoDescription =
    "Use SatStacker's Bitcoin DCA calculator to compare regular dollar-cost averaging with Smart Timing using historical Bitcoin price data and the same budget.";

  const canonical = "https://satstacker.app/calculator";

  const calculatorSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Bitcoin DCA Calculator",
    url: canonical,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    description: seoDescription,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    provider: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "SatStacker",
        item: "https://satstacker.app/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Bitcoin DCA Calculator",
        item: canonical,
      },
    ],
  };

  React.useEffect(() => {
    if (didTrackView.current) return;
    didTrackView.current = true;
    afTrack("calculator_viewed", {});
  }, []);

  const effectiveBuy = isCustom ? (Number(customVal) || 0) : buyUsd;

  const monthlyBudget = React.useMemo(() => {
    const a = effectiveBuy;
    if (cadence === "daily")     return a * 30;
    if (cadence === "weekly")    return a * 4;
    return a * 2;
  }, [cadence, effectiveBuy]);

  const MEMBERSHIP_FEE_USD = 1.99;
  const TRADE_FEE_PCT = 0.25;
  const MIN_RECOMMENDED_MONTHLY_BUDGET = 100;

  const estimatedMonthlyTradeFees = monthlyBudget * (TRADE_FEE_PCT / 100);
  const estimatedMonthlyFees = MEMBERSHIP_FEE_USD + estimatedMonthlyTradeFees;
  const estimatedFeePct = monthlyBudget > 0
    ? (estimatedMonthlyFees / monthlyBudget) * 100
    : 0;

  const showFeeWarning = monthlyBudget > 0 && monthlyBudget < MIN_RECOMMENDED_MONTHLY_BUDGET;

  const onCalculate = async (e) => {
    e.preventDefault();
    if (!effectiveBuy || effectiveBuy <= 0) return;
    setLoading(true);
    setErr(null);
    setBiweeklyPct(null);
    afTrack("calculator_submitted", { cadence, buy_usd: effectiveBuy });
    try {
      const base = import.meta.env.VITE_API_BASE_URL || "";
      const url  = `${base}/public/calculator?cadence=${encodeURIComponent(cadence)}&buy_usd=${encodeURIComponent(effectiveBuy)}`;
      const res  = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResult(data);
      setHasRun(true);
      afTrack("calculator_result_seen", {
        cadence,
        buy_usd: effectiveBuy,
        extra_sats: data.extra_sats_total,
        extra_pct: data.extra_pct,
      });
      requestAnimationFrame(() =>
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      );

      // Background fetch to see if bi-weekly beats current cadence
      if (cadence === "daily" || cadence === "weekly") {
        const currentBestPct = data.period_results?.length
          ? Math.max(...data.period_results.map(p => p.extra_pct))
          : data.extra_pct;
        fetch(`${base}/public/calculator?cadence=bi-weekly&buy_usd=${encodeURIComponent(effectiveBuy)}`)
          .then(r => r.ok ? r.json() : null)
          .then(bw => {
            if (!bw) return;
            const bwBestPct = bw.period_results?.length
              ? Math.max(...bw.period_results.map(p => p.extra_pct))
              : bw.extra_pct;
            if (bwBestPct - currentBestPct >= 0.75) {
              setBiweeklyPct(bwBestPct - currentBestPct); // ← the actual improvement over their cadence
            }
          })
          .catch(() => {});
      }
    } catch (e) {
      setErr(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const bestPeriod = result?.period_results?.length
    ? result.period_results.reduce((a, b) => a.extra_pct > b.extra_pct ? a : b)
    : null;

  // Top-level best — used in signup CTA link and localStorage write
  const best = bestPeriod;

  // Filter windows to best period — all stats derived from the same slice
  const bestPeriodStats = React.useMemo(() => {
    if (!result?.period_results || !result?.windows) {
      return {
        extraSats:    result?.extra_sats_total    || 0,
        baselineSats: result?.baseline_sats_total || 0,
        smartSats:    result?.smart_sats_total    || 0,
        numWindows:   result?.num_windows         || 0,
        avgExtraPerWindow: result?.avg_extra_sats_per_window || 0,
      };
    }
    const best = result.period_results.reduce((a, b) => a.extra_pct > b.extra_pct ? a : b);
    const cutoffMs = Date.now() - best.period_days * 86400000;
    const filtered = result.windows.filter(w => new Date(w.window_start).getTime() >= cutoffMs);
    const extraSats    = filtered.reduce((s, w) => s + w.extra_sats,    0);
    const baselineSats = filtered.reduce((s, w) => s + w.baseline_sats, 0);
    const smartSats    = filtered.reduce((s, w) => s + w.smart_sats,    0);
    const numWindows   = filtered.length;
    return {
      extraSats,
      baselineSats,
      smartSats,
      numWindows,
      avgExtraPerWindow: numWindows ? Math.round(extraSats / numWindows) : 0,
    };
  }, [result]);

  const bestPeriodSats = bestPeriodStats.extraSats;

  const animatedExtra = useCountUp(bestPeriodSats);

  return (
    <>
      <Head>
        <title>{seoTitle}</title>

        <meta
          name="description"
          content={seoDescription}
        />

        <link
          rel="canonical"
          href={canonical}
        />

        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={seoTitle}
        />
        <meta
          property="og:description"
          content={seoDescription}
        />
        <meta
          property="og:url"
          content={canonical}
        />
        <meta property="og:site_name" content="SatStacker" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content={seoTitle}
        />
        <meta
          name="twitter:description"
          content={seoDescription}
        />

        <script type="application/ld+json">
          {JSON.stringify(calculatorSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Head>

      <div className="min-h-screen bg-zinc-950 text-white overflow-x-hidden">

      {/* ── Header ── */}
      <div className="px-4 pt-20 pb-6 max-w-lg mx-auto md:pt-10">
        <div className="flex items-center justify-between mb-5">
          <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-full px-3 py-1.5">
            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse flex-shrink-0" />
            <span className="text-xs font-medium text-zinc-400 tracking-wide">Smart Timing · Historical Simulation</span>
          </div>
          {!isLoggedIn && (
            <Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
              Log in
            </Link>
          )}
          {isLoggedIn && (
            <Link to="/dashboard" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
              Dashboard →
            </Link>
          )}
        </div>
        <h1 className="text-3xl font-bold leading-tight mb-2">
          Bitcoin DCA Calculator<br />
          <span className="text-orange-400">
            Compare Regular DCA vs Smart Timing
          </span>
        </h1>

        <p className="text-base text-zinc-300 font-semibold leading-relaxed mb-2">
          How much more Bitcoin could Smart Timing have bought?
        </p>
        <p className="text-sm text-zinc-500 leading-relaxed">
          Enter your DCA schedule. We’ll simulate how Smart Timing could have
          looked for better Bitcoin prices within each buy window.
        </p>

        <div className="mt-4 inline-flex items-center gap-2 rounded-xl border border-orange-500/20 bg-orange-500/5 px-3 py-2">
          <span className="text-orange-400 font-bold text-sm">
            Same budget. Smarter entries.
          </span>
          <span className="text-zinc-500 text-xs">
            historical simulation
          </span>
        </div>
      </div>

      {/* ── Form ── */}
      <div className="px-4 max-w-lg mx-auto">
        <form onSubmit={onCalculate} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 mb-4">

          {/* Cadence */}
          <div className="mb-5">
            <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">How often do you buy?</div>
            <div className="grid grid-cols-3 gap-2">
              {CADENCES.map(({ value, label, sub }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCadence(value)}
                  className={`py-3 rounded-xl flex flex-col items-center gap-0.5 border transition-colors ${
                    cadence === value
                      ? "bg-orange-500 border-orange-500 text-white"
                      : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
                  }`}
                >
                  <span className="text-sm font-semibold">{label}</span>
                  <span className={`text-xs ${cadence === value ? "text-orange-200" : "text-zinc-600"}`}>{sub}</span>
                </button>
              ))}
            </div>
            {(cadence === "daily" || cadence === "weekly") && biweeklyPct !== null && (
              <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                💡 Historically, switching to bi-weekly captured ~{(Math.round(biweeklyPct * 2) / 2).toFixed(1).replace(".0", "")}% more sats than your current schedule — longer windows give Smart Timing more room to find dips. 
                <button type="button" className="text-orange-400 underline" onClick={() => {
                  const biweeklyAmt = Math.round(effectiveBuy * (cadence === "daily" ? 14 : 2));
                  setIsCustom(true);
                  setCustomVal(String(biweeklyAmt));
                  setCadence("bi-weekly");
                }}>Try bi-weekly →</button>
              </p>
            )}
          </div>

          {/* Amount */}
          <div className="mb-5">
            <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">How much per buy?</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {PRESETS.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => { setBuyUsd(amt); setIsCustom(false); setCustomVal(""); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    !isCustom && buyUsd === amt
                      ? "bg-orange-500 border-orange-500 text-white"
                      : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
                  }`}
                >
                  ${amt}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setIsCustom(true)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  isCustom
                    ? "bg-orange-500 border-orange-500 text-white"
                    : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
                }`}
              >
                Custom
              </button>
            </div>

            {isCustom && (
              <div className="flex items-center bg-zinc-950 border border-zinc-700 rounded-xl px-4 py-3 focus-within:border-orange-500 transition-colors mb-2">
                <span className="text-zinc-500 text-lg mr-1">$</span>
                <input
                  autoFocus
                  value={customVal}
                  onChange={(e) => setCustomVal(e.target.value)}
                  inputMode="decimal"
                  placeholder="Enter amount"
                  className="flex-1 bg-transparent text-white text-xl font-semibold outline-none placeholder-zinc-700 min-w-0"
                />
              </div>
            )}

            {effectiveBuy > 0 && (
              <div className="text-xs text-zinc-600">
                ≈ <span className="text-zinc-400">${Math.round(monthlyBudget).toLocaleString()}/mo</span> total budget
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || effectiveBuy <= 0}
            className="w-full py-4 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-base transition-colors"
          >
            {loading ? "Simulating…" : "See my Smart Timing result →"}
          </button>

          {err && <p className="mt-3 text-xs text-red-400 text-center">{err}</p>}
        </form>

        {/* ── Results ── */}
        <div ref={resultRef} className="scroll-mt-20 md:scroll-mt-4">
          {hasRun && result && (
            <div className="space-y-3 mb-8">

              {(() => {
                const best = result.period_results?.length
                  ? result.period_results.reduce((a, b) => a.extra_pct > b.extra_pct ? a : b)
                  : null;
                const periodLabel = best?.period_days === 91 ? "3 months" : best?.period_days === 182 ? "6 months" : best?.period_days === 273 ? "9 months" : "12 months";
                const sens = result.timing_sensitivity;
                const sensLabel = sens?.period_days === 91 ? "3 months" : sens?.period_days === 182 ? "6 months" : sens?.period_days === 273 ? "9 months" : sens?.period_days === 365 ? "12 months" : null;
                return (
                  <div className="bg-zinc-900 border border-orange-500/20 rounded-3xl px-5 py-6 text-center shadow-[0_0_40px_rgba(249,115,22,0.06)]">
                    <div className="text-xs text-zinc-500 uppercase tracking-widest mb-3">
                      Sats difference · last {periodLabel}
                    </div>
                    <div className="text-5xl font-bold text-orange-400 tabular-nums mb-1">
                      {formatSignedSats(animatedExtra)}
                    </div>
                    <div className="text-sm text-zinc-500 mb-1">
                      vs scheduled DCA
                    </div>

                    <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-2">
                      <span className="text-orange-400 font-bold text-sm">
                        {formatSignedPct(best?.extra_pct ?? result.extra_pct)}
                      </span>
                      <span className="text-zinc-500 text-xs">
                        vs scheduled DCA
                      </span>
                    </div>
                    {sens && sensLabel && (
                      <div className="text-xs text-zinc-500 mt-3">
                        Historical range:{" "}
                        <span className="text-zinc-300">{formatSignedPct(sens.extra_pct_min)}</span>
                        {" to "}
                        <span className="text-zinc-300">{formatSignedPct(sens.extra_pct_max)}</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              <p className="text-xs text-zinc-500 leading-relaxed px-1">
                Smart Timing is{" "}
                <span className="text-zinc-300 font-semibold">$1.99/mo</span>{" "}
                plus{" "}
                <span className="text-zinc-300 font-semibold">0.25% per trade</span>.
              </p>

              {/* ── Signup CTA ── */}
              {!isLoggedIn ? (
                <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-5">
                  <Link
                    to={(() => {
                      try {
                        const pct = best?.extra_pct ?? result?.extra_pct;
                        return `/signup?amount=${encodeURIComponent(effectiveBuy)}&freq=${encodeURIComponent(cadence)}&extra_sats=${encodeURIComponent(bestPeriodSats ?? 0)}&extra_pct=${encodeURIComponent(pct != null ? Number(pct).toFixed(2) : "0")}`;
                      } catch { return "/signup"; }
                    })()}
                    onClick={() => {
                      afTrack("calculator_signup_tapped", {
                        cadence,
                        buy_usd: effectiveBuy,
                        extra_sats: result?.extra_sats_total,
                        extra_pct: result?.extra_pct,
                      });
                      try {
                        localStorage.setItem("calc_prefill", JSON.stringify({
                          amount: effectiveBuy,
                          freq: cadence,
                          extra_sats: bestPeriodSats,
                          extra_pct: (best?.extra_pct ?? result.extra_pct),
                        }));
                      } catch {}
                    }}
                    className="block w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-base text-center transition-colors"
                  >
                    Create my free account →
                  </Link>
                  <p className="text-center text-xs text-zinc-600 mt-3">
                    Already have an account?{" "}
                    <Link to="/login" className="text-zinc-400 hover:text-white underline transition-colors">
                      Log in
                    </Link>
                  </p>
                </div>
              ) : (
                <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-5 text-center">
                  <p className="text-base font-bold text-white mb-3">Ready to stack these sats?</p>
                  <Link to="/dashboard" className="block w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-base text-center transition-colors">
                    Go to Dashboard →
                  </Link>
                </div>
              )}

              {/* Stats grid */}
              <SatsComparison
                baselineSats={bestPeriodStats.baselineSats}
                smartSats={bestPeriodStats.smartSats}
                extraSats={bestPeriodStats.extraSats}
              />

              <details className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
                <summary className="cursor-pointer text-sm font-semibold text-zinc-300">
                  Show simulation details
                </summary>

                <div className="grid grid-cols-2 gap-3 mt-4">
                  <StatCard
                    label={`Avg difference / ${cadence === "bi-weekly" ? "2 wks" : cadence === "weekly" ? "week" : "day"}`}
                    value={formatSignedSats(bestPeriodStats.avgExtraPerWindow)}
                    sub="sats"
                    highlight={false}
                  />
                  <StatCard
                    label="Windows simulated"
                    value={bestPeriodStats.numWindows}
                    sub="buys"
                    highlight={false}
                  />
                </div>
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900 px-4 py-3">
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Smart Timing is designed for committed DCA stackers. Because membership includes a fixed monthly cost, it generally makes the most sense for buyers stacking around $100+/mo.
                  </p>
                </div>
              </details>

              {/* Disclaimer */}
              <p className="text-xs text-zinc-700 leading-relaxed px-1">
                Simulation uses real hourly price data with the actual Smart Timing
                tranche logic.
                Assumes full funds available each window.
                Past performance doesn't guarantee future results.
              </p>

              {/* Reference chart */}
              <div>
                <div className="text-xs text-zinc-600 mb-2 px-1">Recent activity reference</div>
                <ReferenceChart cadence={cadence} monthlyBudgetUsd={monthlyBudget} />
              </div>
            </div>
          )}
        </div>

        {!isLoggedIn && (
          <section className="max-w-3xl mx-auto px-5 pt-12 pb-16">
            <div className="border-t border-zinc-800 pt-12">
              <p className="text-sm uppercase tracking-widest text-orange-400 mb-3">
                Bitcoin DCA Calculator
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                How this Bitcoin DCA calculator works
              </h2>

              <p className="text-zinc-400 leading-7 mb-5">
                This calculator compares a regular Bitcoin dollar-cost averaging
                schedule with SatStacker's Smart Timing using the same recurring
                budget. Choose how often you normally buy Bitcoin and how much
                you invest per purchase, then run the historical simulation.
              </p>

              <p className="text-zinc-400 leading-7 mb-8">
                Regular DCA is modeled as buying at the scheduled end of each
                buy window. Smart Timing uses SatStacker's execution logic inside
                those same buying windows. The comparison is designed to show
                how execution timing could have affected the number of sats
                accumulated without increasing the planned budget.
              </p>

              <div className="grid md:grid-cols-3 gap-4 mb-12">
                {[
                  {
                    n: "1",
                    title: "Choose your DCA schedule",
                    body: "Select daily, weekly, or bi-weekly Bitcoin purchases.",
                  },
                  {
                    n: "2",
                    title: "Enter your buy amount",
                    body: "Use the same recurring amount you would normally allocate to Bitcoin.",
                  },
                  {
                    n: "3",
                    title: "Compare the simulation",
                    body: "See scheduled DCA and Smart Timing results using historical Bitcoin price data.",
                  },
                ].map((item) => (
                  <div
                    key={item.n}
                    className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5"
                  >
                    <div className="text-orange-400 font-bold mb-2">
                      {item.n}
                    </div>
                    <h3 className="font-semibold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-zinc-400 leading-6">
                      {item.body}
                    </p>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-bold mb-4">
                What does the calculator compare?
              </h2>

              <p className="text-zinc-400 leading-7 mb-5">
                The calculator compares two ways of executing the same Bitcoin
                DCA budget: buying on the regular schedule and using Smart
                Timing inside each scheduled buy window. It does not assume
                that you invest extra money when Bitcoin falls.
              </p>

              <p className="text-zinc-400 leading-7 mb-8">
                That distinction matters because spending more during dips can
                produce more Bitcoin simply because more dollars were invested.
                This calculator is intended to compare execution timing while
                keeping the recurring budget consistent.
              </p>

              <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6 mb-12">
                <h2 className="text-xl font-bold mb-3">
                  Historical simulation, not a prediction
                </h2>

                <p className="text-zinc-400 leading-7">
                  The calculator uses historical Bitcoin market data to show how
                  the two approaches would have behaved in past periods. Past
                  performance does not guarantee future results, and Smart
                  Timing cannot guarantee that every buy will beat a fixed DCA
                  purchase.
                </p>
              </div>

              <h2 className="text-2xl font-bold mb-5">
                Bitcoin DCA calculator FAQ
              </h2>

              <div className="space-y-4 mb-12">
                {[
                  {
                    q: "What is a Bitcoin DCA calculator?",
                    a: "A Bitcoin DCA calculator helps model recurring Bitcoin purchases made on a fixed schedule. This calculator also compares those scheduled purchases with SatStacker's Smart Timing using the same recurring budget.",
                  },
                  {
                    q: "What does DCA mean in Bitcoin?",
                    a: "DCA stands for dollar-cost averaging. It means buying a fixed dollar amount of Bitcoin at regular intervals rather than trying to choose one perfect entry price.",
                  },
                  {
                    q: "Can I compare daily, weekly, and bi-weekly Bitcoin DCA?",
                    a: "Yes. The calculator supports daily, weekly, and bi-weekly schedules so you can model the cadence that most closely matches your normal Bitcoin buying plan.",
                  },
                  {
                    q: "Does the calculator guarantee Smart Timing will buy more Bitcoin?",
                    a: "No. The results are historical simulations. Different future price paths can produce different outcomes, and no timing strategy can guarantee better execution in every buying window.",
                  },
                ].map((item) => (
                  <div
                    key={item.q}
                    className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5"
                  >
                    <h3 className="font-semibold text-white mb-2">
                      {item.q}
                    </h3>
                    <p className="text-zinc-400 leading-7">
                      {item.a}
                    </p>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-bold mb-5">
                Learn more about Bitcoin DCA
              </h2>

              <div className="space-y-3">
                <Link
                  to="/bitcoin-dca"
                  className="block text-orange-400 hover:underline"
                >
                  How to DCA Bitcoin →
                </Link>

                <Link
                  to="/smart-dca"
                  className="block text-orange-400 hover:underline"
                >
                  How Smart DCA Works →
                </Link>

                <Link
                  to="/articles/dca-vs-lump-sum"
                  className="block text-orange-400 hover:underline"
                >
                  Bitcoin DCA vs Lump Sum →
                </Link>

                <Link
                  to="/blog/smart-dca-vs-regular-dca"
                  className="block text-orange-400 hover:underline"
                >
                  Smart DCA vs Regular DCA Case Study →
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  </>
);
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, highlight }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="text-xs text-zinc-600 mb-1">{label}</div>
      <div className={`text-lg font-bold truncate ${highlight ? "text-orange-400" : "text-zinc-200"}`}>
        {value}
        <span className={`text-xs font-normal ml-1 ${highlight ? "text-orange-500/60" : "text-zinc-600"}`}>{sub}</span>
      </div>
    </div>
  );
}

function SatsComparison({ baselineSats, smartSats, extraSats }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
      <div className="text-xs text-zinc-500 uppercase tracking-widest mb-3">
        Simulation comparison
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-zinc-500">Scheduled DCA</span>
          <span className="font-semibold text-zinc-300 tabular-nums">
            {baselineSats.toLocaleString()} sats
          </span>
        </div>

        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-zinc-500">
            Smart Timing buys
          </span>
          <span className="font-bold text-orange-400 tabular-nums">
            {smartSats.toLocaleString()} sats
          </span>
        </div>

        <div className="h-px bg-zinc-800" />

        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-zinc-300">Difference vs Scheduled DCA</span>
          <span className="font-bold text-orange-400 tabular-nums">
            {formatSignedSats(extraSats)} sats
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Tranche breakdown ────────────────────────────────────────────────────────

function TrancheBreakdown({ windows, cadence }) {
  const counts = React.useMemo(() => {
    const c = { early_gap: 0, tranche: 0, failsafe: 0 };
    for (const w of windows) {
      for (const b of w.buys || []) {
        if (b.label === "early_gap")       c.early_gap++;
        else if (b.label === "failsafe" || b.label === "end_of_window") c.failsafe++;
        else                               c.tranche++;
      }
    }
    return c;
  }, [windows]);

  const total = counts.early_gap + counts.tranche + counts.failsafe;
  if (total === 0) return null;

  const items = [
    {
      label: "Caught an early dip",
      count: counts.early_gap,
      color: "bg-orange-400",
    },
    {
      label: "Bought during a dip",
      count: counts.tranche,
      color: "bg-orange-500",
    },
    {
      label: "Bought before window ended",
      count: counts.failsafe,
      color: "bg-zinc-600",
    },
  ].filter(i => i.count > 0);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="text-xs text-zinc-500 mb-3">How buys were triggered</div>
      <div className="space-y-2">
        {items.map(({ label, count, color }) => (
          <div key={label} className="flex items-center gap-3">
            <div className="flex items-center gap-2 w-32 flex-shrink-0">
              <span className={`w-2 h-2 rounded-full ${color} flex-shrink-0`} />
              <span className="text-xs text-zinc-400">{label}</span>
            </div>
            <div className="flex-1 bg-zinc-800 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-full ${color} rounded-full`}
                style={{ width: `${Math.round((count / total) * 100)}%` }}
              />
            </div>
            <span className="text-xs text-zinc-500 w-6 text-right">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Reference chart ──────────────────────────────────────────────────────────

function ReferenceChart({ cadence, monthlyBudgetUsd }) {
  const wrapRef = React.useRef(null);
  const [payload, setPayload] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [w, setW] = React.useState(320);

  const H = 210;
  const PAD = { l: 16, r: 16, t: 18, b: 34 };

  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect?.width;
      if (cw) setW(Math.max(280, Math.floor(cw)));
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  React.useEffect(() => {
    const ctrl = new AbortController();

    async function run() {
      setLoading(true);
      try {
        const base = import.meta.env.VITE_API_BASE_URL || "";
        const url =
          `${base}/public/marketing-chart?venue=coinbase` +
          `&cadence=${encodeURIComponent(cadence)}` +
          `&monthly_budget_usd=${encodeURIComponent(monthlyBudgetUsd)}`;

        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        setPayload(await res.json());
      } catch (e) {
        if (e?.name !== "AbortError") setPayload(null);
      } finally {
        setLoading(false);
      }
    }

    run();
    return () => ctrl.abort();
  }, [cadence, monthlyBudgetUsd]);

  const prices = payload?.prices || [];
  const dca = payload?.dca || [];
  const smart = payload?.smart || [];
  const n = prices.length;

  const safeMs = (value) => {
    if (!value) return null;
    if (typeof value === "number") return value;

    const s = String(value);
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00Z` : s;
    const ms = new Date(normalized).getTime();

    return Number.isFinite(ms) ? ms : null;
  };

  const fmtDate = (value) => {
    const ms = safeMs(value);
    if (!ms) return "";
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
    }).format(new Date(ms));
  };

  const fmtUsdCompact = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return `$${Math.round(n).toLocaleString()}`;
  };

  const chart = React.useMemo(() => {
    if (!prices.length) return null;

    const normalized = prices
      .map((p) => ({
        ts: p.ts,
        ms: safeMs(p.ts),
        price: Number(p.price),
      }))
      .filter((p) => p.ms != null && Number.isFinite(p.price))
      .sort((a, b) => a.ms - b.ms);

    if (normalized.length < 2) return null;

    const priceValues = normalized.map((p) => p.price);
    const minRaw = Math.min(...priceValues);
    const maxRaw = Math.max(...priceValues);
    const pad = (maxRaw - minRaw) * 0.08 || maxRaw * 0.01 || 1;

    const minP = minRaw - pad;
    const maxP = maxRaw + pad;

    const startMs = normalized[0].ms;
    const endMs = normalized[normalized.length - 1].ms;
    const spanMs = Math.max(1, endMs - startMs);

    const chartW = w - PAD.l - PAD.r;
    const chartH = H - PAD.t - PAD.b;

    const xForMs = (ms) =>
      PAD.l + ((ms - startMs) / spanMs) * chartW;

    const yForPrice = (price) =>
      PAD.t + (1 - (price - minP) / (maxP - minP || 1)) * chartH;

    const points = normalized.map((p) => ({
      ...p,
      x: xForMs(p.ms),
      y: yForPrice(p.price),
    }));

    const lineD = points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
      .join(" ");

    const areaD =
      `${lineD} L ${points[points.length - 1].x.toFixed(1)} ${H - PAD.b}` +
      ` L ${points[0].x.toFixed(1)} ${H - PAD.b} Z`;

    const nearestPrice = (ms) => {
      let best = points[0];
      let bestDist = Math.abs(points[0].ms - ms);

      for (const p of points) {
        const d = Math.abs(p.ms - ms);
        if (d < bestDist) {
          best = p;
          bestDist = d;
        }
      }

      return best;
    };

    const smartEventPoint = (ev) => {
      const eventMs =
        safeMs(ev.exec_at) ||
        safeMs(ev.ts) ||
        safeMs(ev.window_start);

      if (!eventMs) return null;

      const nearest = nearestPrice(eventMs);
      const price = Number.isFinite(Number(ev.price))
        ? Number(ev.price)
        : nearest.price;

      return {
        x: xForMs(Math.max(startMs, Math.min(endMs, eventMs))),
        y: yForPrice(price),
        price,
        ts: ev.exec_at || ev.ts || nearest.ts,
      };
    };

    const cadenceDays =
      cadence === "daily"
        ? 1
        : cadence === "weekly"
          ? 7
          : 14;

    const scheduledEventPoint = (ev) => {
      const windowStartMs =
        safeMs(ev.exec_at) ||
        safeMs(ev.ts) ||
        safeMs(ev.window_start);

      if (!windowStartMs) return null;

      const scheduledMs =
        windowStartMs + cadenceDays * 86400000;

      // Do not invent a scheduled marker beyond the available chart data.
      if (scheduledMs < startMs || scheduledMs > endMs) {
        return null;
      }

      const nearest = nearestPrice(scheduledMs);

      return {
        x: xForMs(scheduledMs),
        y: yForPrice(nearest.price),
        price: nearest.price,
        ts: nearest.ts,
      };
    };

    const dcaPoints = dca.map(scheduledEventPoint).filter(Boolean);
    const smartPoints = smart.map(smartEventPoint).filter(Boolean);

    const ticks = [0, 0.33, 0.66, 1].map((t) => {
      const ms = startMs + spanMs * t;
      const nearest = nearestPrice(ms);
      return {
        x: xForMs(ms),
        label: fmtDate(nearest.ts),
      };
    });

    const gridPrices = [0.25, 0.5, 0.75].map((t) => {
      const price = minP + (maxP - minP) * t;
      return {
        y: yForPrice(price),
        label: fmtUsdCompact(price),
      };
    });

    return {
      lineD,
      areaD,
      points,
      dcaPoints,
      smartPoints,
      ticks,
      gridPrices,
    };
  }, [prices, dca, smart, w]);

  if (loading) {
    return (
      <div
        ref={wrapRef}
        className="w-full rounded-2xl border border-zinc-800 bg-zinc-900 p-4 overflow-hidden"
      >
        <div className="mb-3 h-4 w-40 rounded bg-zinc-800 animate-pulse" />
        <div className="h-[210px] rounded-xl bg-zinc-950 animate-pulse" />
      </div>
    );
  }

  if (!chart) {
    return (
      <div
        ref={wrapRef}
        className="w-full rounded-2xl border border-zinc-800 bg-zinc-900 p-4"
      >
        <p className="text-sm text-zinc-500">
          Reference chart unavailable right now.
        </p>
      </div>
    );
  }

  const gradientId = `referenceArea-${cadence.replace(/[^a-z0-9]/gi, "")}`;

  return (
    <div
      ref={wrapRef}
      className="w-full rounded-2xl border border-zinc-800 bg-zinc-900 p-4 overflow-hidden"
    >
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-600">
            Reference chart
          </div>
          <div className="text-sm font-semibold text-zinc-300">
            Scheduled DCA vs Smart Timing
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5 text-zinc-500">
            <span className="h-2 w-2 rounded-full bg-zinc-500" />
            Scheduled DCA
          </span>
          <span className="inline-flex items-center gap-1.5 text-orange-400">
            <span className="h-2 w-2 rounded-full bg-orange-500" />
            Smart Timing
          </span>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${w} ${H}`}
        width="100%"
        height={H}
        className="block rounded-xl bg-zinc-950"
        aria-label="Reference Bitcoin price chart comparing fixed DCA and Smart Timing buys"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* horizontal grid */}
        {chart.gridPrices.map((g, idx) => (
          <g key={`grid-${idx}`}>
            <line
              x1={PAD.l}
              y1={g.y}
              x2={w - PAD.r}
              y2={g.y}
              stroke="#27272a"
              strokeWidth="1"
              opacity="0.7"
            />
          </g>
        ))}

        {/* vertical date ticks */}
        {chart.ticks.map((t, idx) => (
          <g key={`tick-${idx}`}>
            <line
              x1={t.x}
              y1={PAD.t}
              x2={t.x}
              y2={H - PAD.b}
              stroke="#27272a"
              strokeWidth="1"
              strokeDasharray="3 7"
              opacity="0.55"
            />
            <text
              x={t.x}
              y={H - 10}
              textAnchor={idx === 0 ? "start" : idx === chart.ticks.length - 1 ? "end" : "middle"}
              fontSize="10"
              fill="#52525b"
            >
              {t.label}
            </text>
          </g>
        ))}

        {/* price area and line */}
        <path d={chart.areaD} fill={`url(#${gradientId})`} />
        <path
          d={chart.lineD}
          fill="none"
          stroke="#d4d4d8"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* scheduled DCA buys at window end */}
        {chart.dcaPoints.map((p, idx) => (
          <g key={`dca-${idx}`}>
            <circle
              cx={p.x}
              cy={p.y}
              r="4.5"
              fill="#71717a"
            />
            <circle
              cx={p.x}
              cy={p.y}
              r="7"
              fill="none"
              stroke="#a1a1aa"
              strokeOpacity="0.2"
            />
          </g>
        ))}

        {/* Smart Timing buys */}
        {chart.smartPoints.map((p, idx) => (
          <g key={`smart-${idx}`}>
            <circle
              cx={p.x}
              cy={p.y}
              r="5"
              fill="#f97316"
            />
            <circle
              cx={p.x}
              cy={p.y}
              r="8"
              fill="none"
              stroke="#f97316"
              strokeOpacity="0.28"
            />
          </g>
        ))}
      </svg>

      <div className="mt-3 text-xs text-zinc-600">
        Uses historical market data. Scheduled DCA markers are shown at the
        scheduled end of each buy window.
      </div>
    </div>
  );
}